import React, { FC } from 'react';
import moment from 'moment';
import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import { IReportWorkingTime } from '../../../models/types';
import {
  getFormatTimeFromMinutes,
  getFormatDurationFromTimeString,
  formatDurationToHHmm,
  formatTimeWindow,
  formatFactWindow
} from './utils';

import Chart from './Chart';

// График has no intrinsic width (Chart is width:100%), so give the column a real
// width; antd's table is 100% wide, so it also absorbs any leftover space.
// Text columns auto-size to their content (not cramped).
const GRAPH_WIDTH = 550;

// Compact rows via antd's own cell renderer (inline style beats the stylesheet,
// so no !important / global CSS). Body cells stay on one line and clip overflow.
const components = {
  header: {
    cell: (props: any) => <th {...props} style={{ ...props.style, padding: '2px 8px' }} />
  },
  body: {
    cell: (props: any) => (
      <td {...props} style={{ ...props.style, padding: '2px 8px', whiteSpace: 'nowrap', overflow: 'hidden' }} />
    )
  }
};

// `render` reads from `record`, so `dataIndex` is omitted.
const WORKSHIFT_COLUMNS: ColumnsType<IReportWorkingTime> = [
  {
    title: 'ФИО сотрудника',
    key: 'employeeName',
    ellipsis: true,
    render: (_: any, record: IReportWorkingTime) => record.employeeName || '-'
  },
  {
    title: 'Смена (план)',
    key: 'plan',
    ellipsis: true,
    render: (_: any, record: IReportWorkingTime) =>
      formatTimeWindow(record.planStart, record.planEnd)
  },
  {
    title: 'Смена (факт)',
    key: 'fact',
    ellipsis: true,
    render: (_: any, record: IReportWorkingTime) =>
      formatFactWindow(record.factStart, record.factEnd)
  },
  {
    title: 'Время в рабочей зоне',
    key: 'workZone',
    ellipsis: true,
    render: (_: any, record: IReportWorkingTime) =>
      formatDurationToHHmm(record.workZoneTime) || '—'
  },
  {
    title: 'Время вне рабочей зоны',
    key: 'nonWorkZone',
    ellipsis: true,
    render: (_: any, record: IReportWorkingTime) =>
      formatDurationToHHmm(record.nonWorkZoneTime) || '—'
  },
  {
    title: 'График работ',
    key: 'graphData',
    width: GRAPH_WIDTH,
    render: (_: any, record: IReportWorkingTime) => <Chart graphData={record.graphData} />
  }
];

const PERIOD_COLUMNS: ColumnsType<IReportWorkingTime> = [
  {
    title: 'Начало смены',
    key: 'start',
    ellipsis: true,
    render: (_: any, record: IReportWorkingTime) =>
      record.start ? moment(record.start).format('DD.MM.YYYY HH:mm') : '-'
  },
  {
    title: 'Окончание смены',
    key: 'end',
    ellipsis: true,
    render: (_: any, record: IReportWorkingTime) =>
      record.end ? moment(record.end).format('DD.MM.YYYY HH:mm') : '-'
  },
  {
    title: 'ФИО сотрудника',
    key: 'employeeName',
    ellipsis: true,
    render: (_: any, record: IReportWorkingTime) => record.employeeName || '-'
  },
  {
    title: 'График работ',
    key: 'graphData',
    width: GRAPH_WIDTH,
    render: (_: any, record: IReportWorkingTime) => <Chart graphData={record.graphData} />
  },
  {
    title: 'Длительность смены',
    key: 'duration',
    ellipsis: true,
    render: (_: any, record: IReportWorkingTime) =>
      record.duration ? getFormatTimeFromMinutes(record.duration) : '-'
  },
  {
    title: 'Перерыв',
    key: 'pause',
    ellipsis: true,
    render: (_: any, record: IReportWorkingTime) =>
      record.nonworkingTime ? getFormatDurationFromTimeString(record.nonworkingTime) : '-'
  },
  {
    title: 'Фактическое время работы',
    key: 'durationFact',
    ellipsis: true,
    render: (_: any, record: IReportWorkingTime) =>
      record.workingTime ? getFormatDurationFromTimeString(record.workingTime) : '-'
  }
];

interface IProps {
  list: IReportWorkingTime[];
}

const List: FC<IProps> = ({ list }) => {
  const isWorkshiftMode = list.length > 0 && Boolean(list[0].timeTableId);

  return (
    <Table<IReportWorkingTime>
      components={components}
      style={{ marginTop: '1em', overflow: 'auto' }}
      size="small"
      rowKey="id"
      pagination={false}
      columns={isWorkshiftMode ? WORKSHIFT_COLUMNS : PERIOD_COLUMNS}
      dataSource={list}
    />
  );
};

export default List;
